import React, { useState, useEffect, useContext } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Chip,
  Grid,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { AuthContext } from '../../context/AuthContext';
import axios from '../../api/client';
import EmptyState from '../EmptyState';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ApprovalActions from '../approval/ApprovalActions';
import ApprovalHistory from '../approval/ApprovalHistory';
import { ApprovalChip } from '../approval/ApprovalChip';
import { useCurrency } from '../../context/CurrencyContext';
import RequisitionIssueAction from './RequisitionIssueAction';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const EMPTY_FORM = {
  purpose: '',
  description: '',
  amount: '',
  neededBy: '',
  propertyId: '',
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'pending_approval', label: 'Pending Final Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const RequisitionManagement = () => {
  const { formatMoney } = useCurrency();
  const { can, user } = useContext(AuthContext);
  const [requisitions, setRequisitions] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server must search there instead.
  const {
    query, setQuery, reset,
    rows: visibleRequisitions, totalCount
  } = useTableSearch(requisitions, {
    searchFields: ['requisitionNumber', 'title', 'purpose', 'property.propertyName'],
    filters: {}
  });
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    fetchRequisitions();
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequisitions = async () => {
    try {
      const response = await axios.get('/requisitions');
      setRequisitions(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching requisitions');
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/properties');
      setProperties(response.data || []);
    } catch (error) {
      // Property is optional on a requisition, so this failing is not fatal.
      setProperties([]);
    }
  };

  const handleOpen = (requisition = null) => {
    if (requisition) {
      setEditing(requisition._id);
      setFormData({
        purpose: requisition.purpose || '',
        description: requisition.description || '',
        amount: requisition.amount ?? '',
        neededBy: requisition.neededBy
          ? format(new Date(requisition.neededBy), 'yyyy-MM-dd')
          : '',
        propertyId: requisition.property?._id || requisition.propertyId || '',
      });
    } else {
      setEditing(null);
      setFormData(EMPTY_FORM);
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.purpose.trim()) {
      toast.error('Please give the requisition a purpose');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }

    const payload = {
      purpose: formData.purpose.trim(),
      description: formData.description.trim() || null,
      amount,
      neededBy: formData.neededBy || null,
      propertyId: formData.propertyId || null,
    };

    try {
      if (editing) {
        await axios.put(`/requisitions/${editing}`, payload);
        toast.success('Requisition updated');
      } else {
        await axios.post('/requisitions', payload);
        toast.success('Requisition raised as a draft — submit it for review when ready');
      }
      setOpen(false);
      fetchRequisitions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving requisition');
    }
  };

  const openPdf = (requisition) => {
    // The endpoint is cookie-authenticated, so a plain window open is enough.
    const base = axios.defaults.baseURL || '';
    window.open(`${base}/requisitions/${requisition._id}/pdf`, '_blank', 'noopener');
  };

  // Chained rather than replaced: the status chips above the table are a separate, quicker
  // control from the toolbar's dropdown, and both should narrow the list rather than one
  // silently overriding the other.
  const visible = visibleRequisitions.filter(
    (r) => statusFilter === 'all' || r.approvalStatus === statusFilter
  );

  const awaitingIssue = requisitions.filter(
    (r) => r.approvalStatus === 'approved' && !r.issuedAt
  ).length;

  // Editing is only possible on your own draft — mirrors the server's rule.
  const canEditRow = (r) =>
    r.approvalStatus === 'draft' &&
    can('requisitions', 'update') &&
    (r.createdBy?._id === user?.id || r.createdById === user?.id || user?.role === 'admin');

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight={700}>Requisitions</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
          {can('requisitions', 'create') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              New Requisition
            </Button>
          )}
        </Box>
      </Box>

      {can('requisitions', 'issue') && awaitingIssue > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {awaitingIssue} approved requisition{awaitingIssue === 1 ? '' : 's'} awaiting release of funds.
        </Alert>
      )}

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Number, title or purpose"
        onReset={reset}
        resultCount={visible.length}
        totalCount={totalCount}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Number</TableCell>
              <TableCell>Purpose</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Funds</TableCell>
              <TableCell>Raised</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r._id}>
                <TableCell className="pin-left" sx={{ fontFamily: 'monospace' }}>{r.requisitionNumber}</TableCell>
                <TableCell>{r.purpose}</TableCell>
                <TableCell>{formatMoney(r.amount)}</TableCell>
                <TableCell>{r.createdBy?.name || '-'}</TableCell>
                <TableCell><ApprovalChip status={r.approvalStatus} /></TableCell>
                <TableCell>
                  {r.issuedAt ? (
                    <Tooltip title={`Released by ${r.issuedBy?.name || 'unknown'}`}>
                      <Chip label="Released" color="success" size="small" variant="outlined" />
                    </Tooltip>
                  ) : (
                    <Chip label="Not released" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  {r.createdAt ? format(new Date(r.createdAt), 'dd MMM yyyy') : '-'}
                </TableCell>
                <TableCell className="pin-right">
                  <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Button size="small" onClick={() => setDetail(r)}>View</Button>
                    {canEditRow(r) && (
                      <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpen(r)}>
                        Edit
                      </Button>
                    )}
                    <Tooltip title="Print requisition form">
                      <IconButton size="small" onClick={() => openPdf(r)}>
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <ApprovalActions
                      item={r}
                      resourcePath="requisitions"
                      permissionModule="requisitions"
                      itemLabel={`${r.requisitionNumber} · ${r.purpose}`}
                      noun="requisition"
                      onChanged={fetchRequisitions}
                    />
                    <RequisitionIssueAction requisition={r} onChanged={fetchRequisitions} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <EmptyState
                    title="No requisitions"
                    description="Requisitions you raise appear here, along with any you are asked to review, approve or pay."
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>

      {/* Create / edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Requisition' : 'New Requisition'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            Saving creates a draft. It only goes for review once you submit it, and the
            amount is locked from that point until someone queries it back.
          </Alert>
          <TextField
            fullWidth
            required
            margin="normal"
            label="Purpose"
            value={formData.purpose}
            onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
            helperText="Short summary, e.g. 'Site visit transport — Acacia Court'"
          />
          <TextField
            fullWidth
            margin="normal"
            multiline
            rows={3}
            label="Details"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                margin="normal"
                type="number"
                label="Amount"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                type="date"
                label="Needed By"
                InputLabelProps={{ shrink: true }}
                value={formData.neededBy}
                onChange={(e) => setFormData({ ...formData, neededBy: e.target.value })}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            select
            margin="normal"
            label="Related Property"
            value={formData.propertyId}
            onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
            helperText="Optional, for reporting only — requisitions are always against agency funds"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {properties.map((p) => (
              <MenuItem key={p._id} value={p._id}>{p.propertyName}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.purpose.trim()}>
            {editing ? 'Update' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail — the approval chain, which is the point of the record */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {detail?.requisitionNumber}
            {detail && <ApprovalChip status={detail.approvalStatus} />}
            {detail?.issuedAt && <Chip label="Funds Released" color="success" size="small" />}
          </Box>
        </DialogTitle>
        <DialogContent>
          {detail && (
            <>
              {detail.lastQueryComment && detail.approvalStatus === 'draft' && (
                <Alert severity="warning" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  <strong>Sent back for changes:</strong> {detail.lastQueryComment}
                </Alert>
              )}

              <Typography variant="h6">{detail.purpose}</Typography>
              <Typography variant="h4" sx={{ my: 1 }}>
                {formatMoney(detail.amount)}
              </Typography>
              {detail.description && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                  {detail.description}
                </Typography>
              )}

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">REQUESTED BY</Typography>
                  <Typography>{detail.createdBy?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">REVIEWED BY</Typography>
                  <Typography>
                    {detail.reviewedBy?.name || '—'}
                    {detail.reviewedAt && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}· {format(new Date(detail.reviewedAt), 'dd MMM yyyy')}
                      </Typography>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">APPROVED BY</Typography>
                  <Typography>
                    {detail.approvedBy?.name || '—'}
                    {detail.approvedAt && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}· {format(new Date(detail.approvedAt), 'dd MMM yyyy')}
                      </Typography>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">FUNDS RELEASED BY</Typography>
                  <Typography>
                    {detail.issuedBy?.name || '—'}
                    {detail.issuedAt && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}· {format(new Date(detail.issuedAt), 'dd MMM yyyy')}
                      </Typography>
                    )}
                  </Typography>
                </Grid>
                {detail.property && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">RELATED PROPERTY</Typography>
                    <Typography>{detail.property.propertyName}</Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="subtitle2" gutterBottom>Audit Trail</Typography>
              <ApprovalHistory
                resourcePath="requisitions"
                itemId={detail._id}
                emptyMessage="Not yet submitted — no activity recorded."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {detail && (
            <Button startIcon={<PictureAsPdfIcon />} onClick={() => openPdf(detail)}>
              Print Form
            </Button>
          )}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RequisitionManagement;
